/**
 * CompanyPaymentAddPage — full-page (NOT modal) creator for a company payment.
 *
 * iter-217c (Feb 2026) — Extracted from the old ServicePayments modal per user
 * request. Key rules:
 *   • Admin selects an entity (from FinanceSettings > جهات الدفع).
 *   • If the entity uses a foreign currency (USD/EUR/…):
 *       – The system shows the fixed exchange rate configured on the entity.
 *       – Admin has TWO options:
 *           (A) Enter the amount in the foreign currency → AED auto-computed
 *               using the entity's fixed rate (read-only).
 *           (B) Enter the AED amount directly → the foreign field stays empty
 *               (the admin already did the conversion themselves).
 *       – Whichever path is used, the FINAL stored value is AED only.
 *   • Receipt image is REQUIRED. It is verified via AI OCR:
 *       – Must be a real receipt (`is_receipt=true`).
 *       – Extracted amount must roughly match the entered amount (±5 AED /
 *         ±2%). If not, the admin is shown a warning and must confirm.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, Loader2, Save, Upload, X, CheckCircle2, AlertTriangle,
  Building2, DollarSign, Calendar, Hash, FileText, ArrowLeftRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { floorAED } from '../../utils/money';
import { DirhamPrice } from '../../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// AED tolerance when comparing the entered amount to the OCR-extracted amount.
const OCR_AED_ABS_TOL     = 5;      // ±5 AED absolute
const OCR_AED_PCT_TOL     = 0.02;   // ±2%
const MAX_RECEIPT_MB      = 5;

// The receipt explicitly shows dirhams (AED / Dhs / د.إ / درهم) next to the total.
const AED_RX = /(AED|DHS?\b|DH\b|د\.?\s?إ|درهم)/i;
const receiptSaysAed = (d) =>
  String(d?.currency || '').toUpperCase() === 'AED'
  && (d?.currency_confidence === 'high' || AED_RX.test(String(d?.currency_evidence || '')));

const EMPTY_FORM = () => ({
  entity_id:      '',
  input_mode:     'aed',   // 'aed' | 'foreign'  (which field is authoritative)
  foreign_amount: '',
  aed_amount:     '',
  payment_date:   new Date().toISOString().split('T')[0],
  invoice_number: '',
  reason_id:      '',
  notes:          '',
});

export default function CompanyPaymentAddPage() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEntryMode = (searchParams.get('mode') || '').toLowerCase() === 'entry';
  const listPath = isEntryMode ? '/finance/asb606/payments/company?mode=entry' : '/finance/asb606/payments/company';
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  // Stable header getter (avoids the infinite re-render bug from iter-217b).
  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const [entities, setEntities]   = useState([]);
  const [reasons,  setReasons]    = useState([]);   // القوائم المنسدلة لأسباب الدفع (اختياري)
  const [loading,  setLoading]    = useState(true);
  const [saving,   setSaving]     = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [savedCount, setSavedCount] = useState(0);   // invoices saved in this session

  // Form
  const [form, setForm] = useState(EMPTY_FORM);
  const [currencyNote, setCurrencyNote] = useState(null);  // 'receipt-aed' | 'receipt-foreign' | 'entity-default'

  // Receipt state
  const [receipt, setReceipt] = useState(null);              // {file, dataUrl, mime, name, size}
  const [ocr,     setOcr]     = useState(null);              // {is_receipt, amount, ...}
  const [ocrConfirmed, setOcrConfirmed] = useState(false);   // admin confirmed mismatch OK

  // ── Load reference data ─────────────────────────────────────────
  const loadRefs = useCallback(async () => {
    setLoading(true);
    try {
      // iter-217c — `payment-services` is the collection used everywhere else
      // for company payment entities (Google, AWS, MongoDB, …). Each row has
      // {name_ar/name_en, currency, exchange_rate}. That's the single source
      // of truth for both this page and CompanyPaymentEntitiesPage.
      const [srv, rsn] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/finance/payment-services`, { headers: authHeaders() }).catch(() => ({ data: { data: [] } })),
        axios.get(`${BACKEND_URL}/api/finance/payment-reasons`,  { headers: authHeaders() }).catch(() => ({ data: { data: [] } })),
      ]);
      setEntities(srv?.data?.data || []);
      setReasons(rsn?.data?.data || []);
    } catch (err) {
      toast.error(t('فشل تحميل الجهات', 'Failed to load entities'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { window.scrollTo(0, 0); loadRefs(); }, [loadRefs]);

  // ── Derived values ──────────────────────────────────────────────
  const entity = useMemo(
    () => entities.find(e => e.id === form.entity_id) || null,
    [entities, form.entity_id],
  );
  const entityCurrency = useMemo(() => {
    const cur = entity?.currency;
    return (cur && cur !== 'AED') ? cur : 'AED';
  }, [entity]);
  const entityRate = useMemo(() => {
    if (entityCurrency === 'AED') return 1;
    const r = parseFloat(entity?.exchange_rate);
    return Number.isFinite(r) && r > 0 ? r : null;
  }, [entity, entityCurrency]);
  const isForeign = entityCurrency !== 'AED';

  // Amount reconciliation
  const finalAedAmount = useMemo(() => {
    if (!isForeign)                    return parseFloat(form.aed_amount) || 0;
    if (form.input_mode === 'aed')     return parseFloat(form.aed_amount) || 0;
    if (form.input_mode === 'foreign' && entityRate) {
      const f = parseFloat(form.foreign_amount) || 0;
      return floorAED(f * entityRate);
    }
    return 0;
  }, [isForeign, form, entityRate]);

  // OCR mismatch check (iter-217c-v9: currency-aware). When the receipt is
  // in the entity's foreign currency, convert `ocr.amount` to AED via
  // `entityRate` before comparing against `finalAedAmount`. Otherwise the
  // raw USD number would look like a huge AED gap and falsely block Save.
  const ocrMismatch = useMemo(() => {
    if (!ocr || !ocr.is_receipt) return null;
    const extracted = Number(ocr.amount) || 0;
    if (!extracted) return null;
    // The receipt amount is in whatever currency the form is using (the AI
    // auto-fill followed the same rule), so convert only in foreign mode.
    const extractedAed = (isForeign && form.input_mode === 'foreign' && entityRate) ? floorAED(extracted * entityRate) : extracted;
    const diff = Math.abs(extractedAed - finalAedAmount);
    const tolerance = Math.max(OCR_AED_ABS_TOL, finalAedAmount * OCR_AED_PCT_TOL);
    if (diff <= tolerance) return null;
    return { extracted: extractedAed, entered: finalAedAmount, diff: floorAED(diff).toFixed(2) };
  }, [ocr, finalAedAmount, isForeign, entityRate, form.input_mode]);

  const canSubmit = (
    !!entity
    && finalAedAmount > 0
    && !!receipt
    && (!ocrMismatch || ocrConfirmed)
    && ocr?.is_receipt === true
  );

  // ── Actions ─────────────────────────────────────────────────────
  const handleReceiptChoose = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RECEIPT_MB * 1024 * 1024) {
      toast.error(t(`الملف أكبر من ${MAX_RECEIPT_MB}MB`, `File is larger than ${MAX_RECEIPT_MB}MB`));
      return;
    }
    // Read as data-URL for preview + base64 for OCR.
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setReceipt({ file, dataUrl, mime: file.type, name: file.name, size: file.size });
      setOcr(null);
      setOcrConfirmed(false);
      // Fire OCR right away.
      setOcrLoading(true);
      try {
        const b64 = dataUrl.split(',', 2)[1];
        const r = await axios.post(
          `${BACKEND_URL}/api/admin/assigned-tasks/receipts/extract`,
          { file_base64: b64, mime: file.type },
          { headers: authHeaders() },
        );
        const data = r.data?.data || null;
        setOcr(data);
        if (!(data?.is_receipt)) {
          toast.warning(t('لم يتم التعرف على الملف كإيصال', 'File was not recognised as a receipt'));
          return;
        }

        // Auto-fill form fields from AI extraction.
        // Rules:
        //   • date       → form.payment_date  (already YYYY-MM-DD from prompt)
        //   • reference  → form.invoice_number
        //   • amount     → currency-aware (Jun 2026):
        //       - AED entity → AED.
        //       - foreign entity + receipt explicitly in dirhams → AED, NO conversion.
        //       - foreign entity + receipt in entity currency / unclear → entity currency (× rate).
        const amt = Number(data.amount) || 0;
        const ocrCurr = String(data.currency || '').toUpperCase();
        const aedFromReceipt = isForeign && receiptSaysAed(data);
        let note = null;
        if (amt > 0 && isForeign) note = aedFromReceipt ? 'receipt-aed' : (ocrCurr === entityCurrency ? 'receipt-foreign' : 'entity-default');
        setForm((prev) => {
          const next = { ...prev };
          if (data.date && /^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
            next.payment_date = data.date;
          }
          if (data.reference && !prev.invoice_number) {
            next.invoice_number = String(data.reference);
          }
          if (amt > 0) {
            if (!isForeign || aedFromReceipt) {
              next.aed_amount = String(amt); next.foreign_amount = ''; next.input_mode = 'aed';
            } else {
              next.foreign_amount = String(amt); next.aed_amount = ''; next.input_mode = 'foreign';
            }
          }
          return next;
        });
        setCurrencyNote(note);

        // Friendly toast telling the admin what got auto-filled.
        const filled = [];
        if (data.date) filled.push(t('التاريخ', 'date'));
        if (data.reference) filled.push(t('رقم الحوالة', 'reference'));
        if (data.amount) filled.push(t('المبلغ', 'amount'));
        if (filled.length) {
          toast.success(
            t(`✨ تعبئة تلقائية: ${filled.join(' + ')}`, `✨ Auto-filled: ${filled.join(' + ')}`)
          );
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || t('فشل التحقق من الإيصال بالذكاء الاصطناعي', 'AI receipt verification failed'));
      } finally {
        setOcrLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const selectedReason = reasons.find(r => r.id === form.reason_id);
      await axios.post(`${BACKEND_URL}/api/finance/service-payments`, {
        service_type:   entity.id,
        amount:         isForeign && form.input_mode === 'foreign'
                          ? (parseFloat(form.foreign_amount) || 0)
                          : finalAedAmount,
        amount_aed:     finalAedAmount,
        currency:       isForeign && form.input_mode === 'foreign' ? entityCurrency : 'AED',
        rate_used:      isForeign && form.input_mode === 'foreign' ? entityRate : 1,
        reason_id:      form.reason_id || null,
        reason_name_ar: selectedReason?.name_ar || null,
        reason_name_en: selectedReason?.name_en || null,
        description:    selectedReason?.name_ar || null,
        payment_date:   form.payment_date,
        invoice_number: form.invoice_number,
        // Store the receipt as a base64 data-URL directly (same as pre-iter-217b).
        invoice_file_url: receipt?.dataUrl || '',
        notes:          form.notes,
        // Extra audit — the OCR result at time of submission.
        ocr_verified:   true,
        ocr_amount:     ocr?.amount || null,
        ocr_reference:  ocr?.reference || null,
        ocr_currency:   ocr?.currency || null,
        currency_source: currencyNote === 'receipt-aed' || currencyNote === 'receipt-foreign' ? 'receipt' : 'entity',
      }, { headers: authHeaders() });

      toast.success(t('تمت إضافة الفاتورة بنجاح — يمكنك إضافة فاتورة جديدة', 'Invoice added successfully — you can add another one'));
      // Stay on the page, reset for the next invoice (keep the same entity).
      setSavedCount((n) => n + 1);
      setForm({ ...EMPTY_FORM(), entity_id: form.entity_id });
      setReceipt(null); setOcr(null); setOcrConfirmed(false); setCurrencyNote(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('حدث خطأ أثناء الإضافة', 'Error adding payment'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <DashboardLayout
      title="إضافة فاتورة للشركة"
      titleEn="Add Company Invoice"
      subtitle="فاتورة جديدة (خدمات، اشتراكات، ...) — الحفظ النهائي بالدرهم"
      subtitleEn="New invoice (services, subscriptions, …) — final stored value is AED"
      screenCode="ASB-FIN-PAY-CO-ADD"
      backPath={listPath}
      onBack={() => navigate(listPath)}
      breadcrumb={[
        { labelAr: 'المالية',           labelEn: 'Finance',            path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)',  labelEn: 'ASB606 (Finance)',   path: '/finance/asb606' },
        isEntryMode
          ? { labelAr: 'الإدراج المالي للشركة', labelEn: 'Financial Entry', path: '/finance/asb606/payments/entry' }
          : { labelAr: 'المدفوعات',     labelEn: 'Payments',           path: '/finance/asb606/payments' },
        { labelAr: 'مدفوعات الشركة',     labelEn: 'Company Payments',   path: listPath },
        { labelAr: 'إضافة فاتورة',       labelEn: 'Add Invoice' },
      ]}
    >
      <main className="relative z-10 py-6 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" dir={isAr ? 'rtl' : 'ltr'} data-testid="company-payment-add-page">
        {savedCount > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100" data-testid="saved-count-banner">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{t(`تم حفظ ${savedCount} فاتورة في هذه الجلسة — النموذج جاهز لفاتورة جديدة`, `${savedCount} invoice(s) saved this session — form is ready for the next one`)}</span>
            <button onClick={() => navigate(isEntryMode ? '/finance/asb606/payments/company?mode=records' : listPath)} className="text-xs underline text-emerald-200 hover:text-white" data-testid="go-to-records-link">{t('عرض السجل', 'View records')}</button>
          </div>
        )}
        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white/40" />
          </div>
        ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* 1. Entity picker */}
          <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
            <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
              <Building2 className="w-4 h-4 text-blue-400" />
              {t('اختر الجهة', 'Choose Entity')} *
            </label>
            <select
              value={form.entity_id}
              onChange={(e) => {
                setForm(f => ({ ...f, entity_id: e.target.value, foreign_amount: '', aed_amount: '' }));
              }}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              style={{ colorScheme: 'dark' }}
              data-testid="entity-select"
            >
              <option value="">{t('— اختر —', '— Select —')}</option>
              {entities.map(en => (
                <option key={en.id} value={en.id}>
                  {isAr ? (en.name_ar || en.name) : (en.name_en || en.name)}
                  {en.currency && en.currency !== 'AED' ? `  (${en.currency})` : ''}
                </option>
              ))}
            </select>
            {entity && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white/5 rounded-lg px-3 py-2 border border-white/5">
                  <div className="text-white/50">{t('العملة', 'Currency')}</div>
                  <div className="text-white font-bold">{entityCurrency}</div>
                </div>
                <div className="bg-white/5 rounded-lg px-3 py-2 border border-white/5">
                  <div className="text-white/50">{t('سعر الصرف مقابل الدرهم', 'Rate → AED')}</div>
                  <div className="text-white font-bold" dir="ltr">
                    {isForeign ? (entityRate ? <>1 {entityCurrency} = <DirhamPrice value={entityRate} decimals={2} size="sm" /></> : t('غير محدّد', 'Not set')) : '—'}
                  </div>
                </div>
              </div>
            )}
            {isForeign && !entityRate && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10">
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5" />
                <p className="text-xs text-amber-100">
                  {t('لم يُحدَّد سعر الصرف لهذه الجهة. عدّلها من: المالية ▸ إعدادات القسم المالي ▸ جهات الدفع.',
                     'No exchange rate set. Edit it from: Finance ▸ Settings ▸ Payment Entities.')}
                </p>
              </div>
            )}
          </section>

          {/* 2. Receipt — AI reads it and auto-fills amount + date + reference.
                iter-217c-v10: reordered per user flow request. Was gated on amount>0
                which contradicts the intended flow (upload → AI fills). */}
          {entity && (
            <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
                <Upload className="w-4 h-4 text-amber-400" />
                {t('إيصال الدفع (يقرأه الذكاء الاصطناعي)', 'Receipt (AI-read)')} *
              </label>
              <p className="text-[11px] text-white/50 mb-3 leading-relaxed">
                {t('ارفع الإيصال، والذكاء الاصطناعي سيقرأ ويعبّئ تلقائياً: المبلغ (بالدرهم إذا الإيصال بالدرهم، أو بعملة الجهة مع التحويل التلقائي إلى الدرهم)، تاريخ الدفع، ورقم الفاتورة.',
                   'Upload the receipt — AI will read and auto-fill: amount (AED if receipt is AED, or entity currency with auto-conversion to AED), payment date, and invoice #.')}
              </p>

              {!receipt ? (
                <label className="flex items-center justify-center h-32 bg-[#0a0a0a] border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-amber-400/40 transition"
                       data-testid="receipt-upload-label">
                  <div className="text-center">
                    <Upload className="w-6 h-6 text-white/40 mx-auto mb-2" />
                    <p className="text-sm text-white/60">{t('اسحب الملف أو اضغط للرفع', 'Drag & drop, or click to browse')}</p>
                    <p className="text-[11px] text-white/40 mt-1">{t(`صورة أو PDF — الحد الأقصى ${MAX_RECEIPT_MB}MB`, `Image or PDF — max ${MAX_RECEIPT_MB}MB`)}</p>
                  </div>
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptChoose} data-testid="receipt-input" />
                </label>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#0a0a0a] border border-white/10">
                    {receipt.mime?.startsWith('image/') ? (
                      <img src={receipt.dataUrl} alt="receipt" className="w-14 h-14 object-cover rounded-lg" />
                    ) : (
                      <FileText className="w-14 h-14 text-blue-400" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{receipt.name}</p>
                      <p className="text-[11px] text-white/50">{(receipt.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => { setReceipt(null); setOcr(null); setOcrConfirmed(false); setCurrencyNote(null); }}
                      className="text-white/50 hover:text-rose-400"
                      data-testid="receipt-remove-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* OCR result */}
                  {ocrLoading ? (
                    <div className="flex items-center gap-2 text-xs text-white/60 p-3 rounded-lg bg-white/5">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('يتحقّق الذكاء الاصطناعي من الإيصال…', 'AI is verifying the receipt…')}
                    </div>
                  ) : ocr ? (
                    ocr.is_receipt ? (
                      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs" data-testid="ocr-result-valid">
                        <div className="flex items-center gap-2 text-emerald-200 font-semibold">
                          <CheckCircle2 className="w-4 h-4" />
                          {t('تم التعرف كإيصال صالح — البيانات معبّأة أدناه', 'Recognised as a valid receipt — data filled below')}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-emerald-100/90">
                          <span>{t('المبلغ المُستخرج', 'Extracted amount')}: <b dir="ltr">{ocr.amount} {ocr.currency || (isForeign ? entityCurrency : 'AED')}</b></span>
                          {ocr.reference && <span>{t('المرجع', 'Reference')}: <b dir="ltr">{ocr.reference}</b></span>}
                          {ocr.date && <span>{t('التاريخ', 'Date')}: <b dir="ltr">{ocr.date}</b></span>}
                        </div>
                        {isForeign && currencyNote && (
                          <div className={`mt-2 px-2.5 py-1.5 rounded-md text-[11px] border ${currencyNote === 'receipt-aed' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-100' : 'bg-blue-500/15 border-blue-500/40 text-blue-100'}`} data-testid="ocr-currency-note">
                            {currencyNote === 'receipt-aed'
                              ? t(`الإيصال بالدرهم (${ocr.currency_evidence || 'AED'}) — تم اعتماد المبلغ بالدرهم مباشرة بدون تحويل من ${entityCurrency}.`, `Receipt is in AED (${ocr.currency_evidence || 'AED'}) — amount taken in AED directly, no ${entityCurrency} conversion.`)
                              : currencyNote === 'receipt-foreign'
                                ? t(`الإيصال بعملة ${entityCurrency} — يُحوَّل إلى الدرهم بسعر الجهة.`, `Receipt is in ${entityCurrency} — converted to AED at the entity rate.`)
                                : t(`لم تُحدَّد عملة الإيصال بوضوح — تم اعتماد عملة الجهة (${entityCurrency}). بدّل إلى "أدخل بالدرهم" إذا كان الإيصال بالدرهم.`, `Receipt currency unclear — entity currency (${entityCurrency}) assumed. Switch to "Enter AED" if the receipt is in dirhams.`)}
                          </div>
                        )}

                        {ocrMismatch && !ocrConfirmed && (
                          <div className="mt-3 p-3 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-100" data-testid="ocr-mismatch">
                            <div className="flex items-center gap-2 font-semibold mb-1">
                              <AlertTriangle className="w-4 h-4" />
                              {t('تنبيه: المبلغ في الإيصال لا يطابق ما أدخلت', 'Warning: entered amount does not match the receipt')}
                            </div>
                            <p className="text-[11px]">
                              {t('الفرق', 'Difference')}: <b dir="ltr"><DirhamPrice value={ocrMismatch.diff} decimals={2} size="sm" /></b> — {t('المدخل', 'Entered')}: <b dir="ltr"><DirhamPrice value={ocrMismatch.entered} decimals={2} size="sm" /></b> · {t('المستخرج', 'Extracted')}: <b dir="ltr"><DirhamPrice value={ocrMismatch.extracted} decimals={2} size="sm" /></b>
                            </p>
                            <button
                              onClick={() => setOcrConfirmed(true)}
                              className="mt-2 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-amber-500/30 border border-amber-400/50 text-amber-50 hover:bg-amber-500/40"
                              data-testid="ocr-confirm-mismatch-btn"
                            >
                              {t('أتحمّل المسؤولية وأتابع', 'I accept and continue')}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs text-rose-100" data-testid="ocr-result-invalid">
                        <div className="flex items-center gap-2 font-semibold mb-1">
                          <AlertTriangle className="w-4 h-4" />
                          {t('لم يتم التعرف كإيصال صالح', 'Not recognised as a valid receipt')}
                        </div>
                        {ocr.reject_reason && <p className="text-[11px] text-rose-200/80">{ocr.reject_reason}</p>}
                        <p className="text-[11px] mt-1">{t('ارفع صورة إيصال أوضح لتتمكن من الحفظ.', 'Please upload a clearer receipt to enable saving.')}</p>
                      </div>
                    )
                  ) : null}
                </div>
              )}
            </section>
          )}

          {/* 3. Amount (auto-filled by AI — editable) */}
          {entity && (
            <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-white">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  {t('المبلغ', 'Amount')} *
                </label>
                {isForeign && (
                  <div className="inline-flex bg-white/5 border border-white/10 rounded-lg p-0.5 text-xs" data-testid="input-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, input_mode: 'aed', foreign_amount: '' }))}
                      className={`px-3 py-1.5 rounded-md font-medium transition ${
                        form.input_mode === 'aed' ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'text-white/60 hover:text-white'
                      }`}
                      data-testid="mode-aed-btn"
                    >
                      {t('أدخل بالدرهم', 'Enter AED')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, input_mode: 'foreign', aed_amount: '' }))}
                      className={`px-3 py-1.5 rounded-md font-medium transition ${
                        form.input_mode === 'foreign' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40' : 'text-white/60 hover:text-white'
                      }`}
                      data-testid={`mode-foreign-btn`}
                    >
                      {t(`أدخل بـ ${entityCurrency}`, `Enter ${entityCurrency}`)}
                    </button>
                  </div>
                )}
              </div>

              {isForeign && form.input_mode === 'foreign' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1.5">
                      {t(`المبلغ بـ ${entityCurrency}`, `Amount in ${entityCurrency}`)}
                    </label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.foreign_amount}
                      onChange={(e) => setForm(f => ({ ...f, foreign_amount: e.target.value }))}
                      dir="ltr"
                      className="bg-[#0a0a0a] border-white/10 text-white h-11"
                      data-testid="foreign-amount-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1.5 inline-flex items-center gap-1">
                      <ArrowLeftRight className="w-3 h-3" />
                      {t('يُحوَّل تلقائياً إلى الدرهم', 'Auto-converted to AED')}
                    </label>
                    <div className="h-11 rounded-md bg-white/5 border border-white/5 flex items-center px-3 text-emerald-300 font-mono" dir="ltr" data-testid="derived-aed-amount">
                      <DirhamPrice value={finalAedAmount} decimals={2} size="sm" />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t('المبلغ بالدرهم', 'Amount in AED')}
                  </label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.aed_amount}
                    onChange={(e) => setForm(f => ({ ...f, aed_amount: e.target.value }))}
                    dir="ltr"
                    className="bg-[#0a0a0a] border-white/10 text-white h-11"
                    data-testid="aed-amount-input"
                  />
                </div>
              )}
            </section>
          )}

          {/* 4. Optional metadata (date + invoice # are AI-filled) */}
          {entity && (
            <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-semibold text-white/80">{t('تفاصيل إضافية', 'Additional details')}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{t('تاريخ الدفع', 'Payment Date')}
                  </label>
                  <Input
                    type="date"
                    value={form.payment_date}
                    onChange={(e) => setForm(f => ({ ...f, payment_date: e.target.value }))}
                    className="bg-[#0a0a0a] border-white/10 text-white h-11"
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5 inline-flex items-center gap-1">
                    <Hash className="w-3 h-3" />{t('رقم الفاتورة', 'Invoice #')}
                  </label>
                  <Input
                    value={form.invoice_number}
                    onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                    dir="ltr"
                    className="bg-[#0a0a0a] border-white/10 text-white h-11"
                    data-testid="invoice-number-input"
                  />
                </div>
              </div>

              {reasons.length > 0 && (
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t('سبب الدفع', 'Reason')}
                  </label>
                  <select
                    value={form.reason_id}
                    onChange={(e) => setForm(f => ({ ...f, reason_id: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 h-11 text-white focus:outline-none focus:border-blue-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="">{t('— اختياري —', '— Optional —')}</option>
                    {reasons.map(r => (
                      <option key={r.id} value={r.id}>{isAr ? r.name_ar : r.name_en}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[11px] text-white/50 mb-1.5 inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" />{t('ملاحظات', 'Notes')}
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </section>
          )}

          {/* 5. Actions */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => navigate(listPath)}
              className="border-white/10 text-white/80 hover:bg-white/5"
              data-testid="cancel-btn"
            >
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 h-11"
              data-testid="submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                <Save className="w-4 h-4 ml-2" />
                {t('حفظ الفاتورة', 'Save Invoice')}
              </>}
            </Button>
          </div>
        </motion.div>
        )}
      </main>
    </DashboardLayout>
  );
}
